import React, { useState, useEffect, useMemo } from 'react';
import './ChatBubble.scss';
import chatMessageIcon from '../../images/icon/icon-chat-message-bubble.svg';
import textChatIcon from '../../images/icon/textchat-d-bubble.svg';
import whatsappIcon from '../../images/icon/whatsapp-d-bubble.svg';
import closeChatIcon from '../../images/icon/icon-close-w.svg';
import language from '../../utils/LanguageSettings';
import translations from '../../text/get-started-translations.json';
import { useAuth } from '../../contexts/AuthProvider';
import { ENV } from '../../config';

export interface ChatOption {
  /** Text label for the chat option */
  label: string;
  /** Link URL for the chat option */
  link: string;
  /** Icon to display for this option (defaults to WhatsApp icon) */
  icon?: string;
  /** Click handler for the chat option */
  onClick?: () => void;
}

export interface ChatBubbleProps {
  /** Whether the chat bubble is visible */
  isVisible?: boolean;
  /** Custom class name for styling */
  className?: string;
  /** Array of chat options to display */
  options?: ChatOption[];
  /** Callback when close button is clicked */
  onClose?: () => void;
}

/** Chat bubble component for customer support */
export const ChatBubble: React.FC<ChatBubbleProps> = ({
  isVisible = true,
  className = '',
  options: initialOptions,
  onClose,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const { isLoggedIn, userData } = useAuth();

  const pageTranslations = translations[language];

  // Helper to build the dynamic live chat link
  const getLiveChatLink = () => {
    const isUAT = ENV === 'development' || ENV === 'staging';
    const baseUrl = isUAT 
      ? 'https://digital-uat.cc.hkt.com/service-cloud/aicc-web/livechat/index.html'
      : 'https://digital.cc.hkt.com/service-cloud/aicc-web/livechat/index.html';
    
    const params = new URLSearchParams();

    // Environment specific IDs
    const tenantSpaceId = isUAT ? '202407261456' : '202502108499';
    
    // Channel Config logic based on Language and Environment
    const channelConfigId = isUAT 
      ? (language === 'zh' ? '202407261456214918' : '202407261456368700')
      : (language === 'zh' ? '202502108499495387' : '202502108499842893');
      
    // Module ID logic based on Language and Environment
    const moduleId = isUAT 
      ? (language === 'zh' ? 'CBG_Web_Test' : 'CBG_Web_Test_E')
      : (language === 'zh' ? 'CBG_Web_Livechat' : 'CBG_Web_Livechat_E');
      
    const entranceSite =  isUAT ? 'https://stag.nowtv.now.com' : 'https://www.nowtv.now.com';


    params.append('tenantSpaceId', tenantSpaceId);
    params.append('channelConfigId', channelConfigId);
    
    // Check if logged in AND has fsa data
    if (isLoggedIn && userData?.fsa) {
      params.append('thirdUserId', userData.fsa);
    } else {
      params.append('thirdUserId', 'Guest');
    }
    params.append('thirdUserName', 'NowTVuser');
    
    params.append('language', language === 'zh' ? 'zh' : 'en');
    params.append('moduleId', moduleId);
    params.append('entranceSite', entranceSite);
    params.append('timestamp', new Date().toISOString());
    params.append('visitSource', 'website');
    params.append('mapService', 'google');

    let urlString = params.toString();
    // Use raw : in timestamp
    urlString = urlString.replace(/(timestamp=[^&]+)/, (m) => m.replace(/%3A/g, ':'));
    // Use raw : and / in entranceSite
    urlString = urlString.replace(/(entranceSite=[^&]+)/, (m) => m.replace(/%3A/g, ':').replace(/%2F/g, '/'));

    return `${baseUrl}?${urlString}`;
  };

  // Default options if not provided
  const defaultOptions = useMemo(() => [
      /*
      { 
        label: pageTranslations.chatBubbleLiveChat, 
        link: getLiveChatLink(), 
        icon: textChatIcon 
      },
      */
      { 
        label: pageTranslations.chatBubbleOnlineSales, 
        link: 'https://api.whatsapp.com/send?phone=85296133533', 
        icon: whatsappIcon 
      },
      { 
        label: pageTranslations.chatBubbleCustomerService, 
        link: 'https://api.whatsapp.com/send?phone=85260551888', 
        icon: whatsappIcon 
      },
  ], [pageTranslations, isLoggedIn, userData, language]);

  const currentOptions = initialOptions || defaultOptions;

  // Check scroll position to show bubble
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 150) {
        setShowBubble(true);
      } else {
        setShowBubble(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check in case page is already scrolled
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Device and Time logic for filtering
  const [filteredOptions, setFilteredOptions] = useState<ChatOption[]>(currentOptions);

  useEffect(() => {
    const checkVisibility = () => {
      const userAgent = navigator.userAgent;
      // catch modern iPads
      const isIPad = /iPad/i.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);    
      // Comprehensive check for all Mobile/Tablet devices
      const isMobileOrTablet = /Mobi|Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Tablet/i.test(userAgent) || isIPad;

      // Time check: Everyday
      // Development/Staging: 10:00 - 17:00 (Hong Kong Time)
      // Production/Others: 09:00 - 22:00 (Hong Kong Time)
      /*
      const now = new Date();
      // Get current hour in Hong Kong (UTC+8)
      const hkHour = parseInt(new Intl.DateTimeFormat('en-GB', {
        hour: 'numeric',
        hour12: false,
        timeZone: 'Asia/Hong_Kong'
      }).format(now));

      const isUAT = ENV === 'development' || ENV === 'staging';
      const startHour = isUAT ? 10 : 9;
      const endHour = isUAT ? 17 : 22;

      const isWithinTimeRange = hkHour >= startHour && hkHour < endHour;

      const showLiveChat = isWithinTimeRange && !isMobileOrTablet;
      */

      const newFiltered = currentOptions.filter(option => {
        // Filter "Live Chat" based on translated labels
        /*
        if (option.label === pageTranslations.chatBubbleLiveChat) {
          return showLiveChat;
        }
        */
        return true;
      });

      setFilteredOptions(newFiltered);
      // Use the mobile/tablet flag for styling the bubble menu behavior (click vs hover)
      setIsMobile(isMobileOrTablet);
    };

    checkVisibility();
  }, [currentOptions, pageTranslations]);

  // Handle document click to close mobile menu
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      // Don't close if clicking on the chat bubble wrapper or close button
      const chatWrapper = document.querySelector('.livechat-btn-wrapper');
      if (chatWrapper && chatWrapper.contains(e.target as Node)) {
        return;
      }
      if (isMobile && isExpanded) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [isMobile, isExpanded]);

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMobile) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleWrapperClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleOptionClick = (option: ChatOption) => (e: React.MouseEvent) => {
    e.preventDefault();
    option.onClick?.();

    // Ensure Live Chat link has a fresh timestamp upon click
    let finalLink = option.link;
    /*
    if (option.label === pageTranslations.chatBubbleLiveChat) {
      finalLink = getLiveChatLink();
    }
    */

    if (finalLink && finalLink !== '#') {
      window.open(finalLink, '_blank');
    }
  };

  const handleCloseClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(false);
    onClose?.();
  };

  if (!isVisible || (onClose && !showBubble)) {
    return null;
  }

  return (
    <div 
      className={`livechat-btn-wrapper ${className} ${isMobile ? 'mobile' : ''} ${showBubble ? 'show' : ''}`}
      onClick={handleWrapperClick}
    >
      <div 
        className={`content ${isExpanded ? 'active' : ''}`}
        onClick={handleContentClick}
      >
        <img src={chatMessageIcon} alt="Chat icon" />
        <div className="text" dangerouslySetInnerHTML={{ __html: pageTranslations.chatBubbleText }} />
        <div className="close" onClick={handleCloseClick} onTouchEnd={handleCloseClick}>
          <img src={closeChatIcon} alt="Close" />
        </div>
      </div>

      <div className={`link-wrapper ${isExpanded ? 'show' : ''}`}>
        <div className="button-wrapper">
          {filteredOptions.map((option, index) => (
            <a 
              key={index}
              href={option.link} 
              target="_blank" 
              rel="noreferrer"
              onClick={handleOptionClick(option)}
            >
              <img src={option.icon || whatsappIcon} alt={option.label} />
              <span dangerouslySetInnerHTML={{ __html: option.label }} />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChatBubble;
